import { Tooltip } from 'antd';
import { getRegion } from '../../../components/regionWrapper';
import helper from '../../../utils/helper';
import { BRAND } from './constants';
import { Region } from '../../../utils/commonInterfaces';

export const renderNa = (value: any) =>
    value !== undefined && value !== null && value !== '' ? value : 'N/A';

export const renderAmount = (value: any) => {
    const region = getRegion();
    return value !== undefined && value !== null && value !== ''
        ? `${region === Region.SE ? `${helper.amountFormatter(value)} SAR` : `${helper.decimalAmountFormatter(value)} EGP`}`
        : 'N/A';
}

export const toExportNumber = (value: any): number | string => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const num = Number(value);
    return Number.isNaN(num) ? 'N/A' : num;
};

export const toExportValue = (value: any): string | number => {
    if (value === null || value === undefined || value === '') return 'N/A';
    return value;
};

export const renderColumnTitle = (english: string, arabic?: string) => {
    if (!arabic) return english;
    return (
        <Tooltip title={`${english} / ${arabic}`}>
            <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.2, alignItems: 'center' }}>
                <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{english}</span>
                <span
                    dir="rtl"
                    style={{
                        fontSize: 16,
                        fontWeight: 400,
                        color: '#8c8c8c',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {arabic}
                </span>
            </span>
        </Tooltip>
    );
};

export const mapUserToExportRow = (user: any) => {
    const region = getRegion();
    return {
    'User ID': toExportValue(user?.userID),
    'Staff ID': toExportValue(user?.staff_id),
        ...(region === Region.EG ? { 'Bank Account Number': toExportValue(user?.bankAccountNumber) } : {}),
    'Employee Name / اسم الموظف': toExportValue(user?.name),
    'National ID / رقم الهويه': toExportValue(user?.nationalID),
    'Status': toExportValue(user?.status),
    'Suspension Flag': user?.isSuspended ? 'Yes' : 'No',
    'Next Month Due Amount / المبلغ المستحق للشهر القادم': toExportNumber(user?.nextMonthDueAmount),
        ...(region === Region.SE ? { 'Current Active Requests / الطلبات النشطة الحالية': toExportValue(user?.activeRequests) } : {}),
    'Remaining Outstanding Amounts / المبالغ المتبقية/المستحقة': toExportNumber(user?.outstandingAmount),
        ...(region === Region.SE ? { 'Remaining Installments ( Months) / الأقساط المتبقية (بالأشهر)': toExportValue(user?.remainingInstallments) } : {}),
    'Total Requests Transferred / إجمالي الطلبات المحوّلة': toExportValue(user?.totalRequestsTransferred),
    'Total Requested Amount ( Including Cancelled & Processing) / إجمالي المبلغ المطلوب (بما في ذلك الملغى وقيد المعالجة)': toExportNumber(user?.totalRequestedAmountAllStatuses),
    'Total Requested Amount Transferred (إجمالي المبلغ المطلوب المحوّل)': toExportNumber(user?.totalRequestedAmountTransferred),
        ...(region === Region.SE ? {
            'Paid Amount / المبلغ المدفوع': toExportNumber(user?.paidAmount),
            'Requests Installments ( Months) / عدد أقساط الطلب (بالأشهر)': toExportValue(user?.requestsInstallments),
        } : {}),
    'Suspension Reason': user?.isSuspended && user?.suspensionReason ? user.suspensionReason : 'N/A',
        ...(region === Region.SE ? { 'Early Settlement Amount / مبلغ السداد المبكر': toExportNumber(user?.earlySettlementAmount) } : {}),
    };
};

// Status pill colours for the table (Pending = amber, Approved = green, Inactive/other = slate).
export const statusPillStyle = (status?: string): { bg: string; color: string } => {
    const s = (status || '').toLowerCase();
    if (s.includes('pending')) return { bg: 'rgba(245, 166, 35, 0.14)', color: '#B7791F' };
    if (s.includes('inactive')) return { bg: 'rgba(143, 163, 174, 0.16)', color: '#5b6b70' };
    if (s.includes('approved')) return { bg: 'rgba(52, 199, 89, 0.14)', color: BRAND.greenDark };
    if (s.includes('active')) return { bg: BRAND.tealSoft, color: BRAND.tealDark };
    return { bg: 'rgba(143, 163, 174, 0.16)', color: '#5b6b70' };
};

// Semantic donut/legend colour per status — kept in sync with the status pills so a
// filtered view (e.g. Pending) shows a single matching colour (Pending = yellow).
export const statusToColor = (status?: string): string => {
    const s = (status || '').toLowerCase();
    if (s.includes('pending')) return BRAND.yellow;
    if (s.includes('inactive')) return BRAND.slate;
    if (s.includes('approved')) return BRAND.green;
    if (s.includes('active')) return BRAND.teal;
    if (s.includes('suspend') || s.includes('block')) return BRAND.red;
    if (s.includes('resign')) return BRAND.rose;
    if (s.includes('refused')) return BRAND.brown;
    if (s.includes('hold')) return BRAND.blue;
    return BRAND.tealDark;
};

// Route a single combined search term to the right backend param.
// Saudi National ID / Iqama = 10 digits starting 1 or 2, phone starts 05 / 9665 / +9665;
// Egypt National ID = 14 digits, phone starts 01[0125] / +201[0125] / 001201[0125],
// bank account number = 11 digits or XX-XXX-XXXXXX; else Staff ID.
export const routeSearchTerm = (raw?: string) => {
    const term = (raw || '').trim();
    const empty = {
        staffID: null as string | null,
        phoneNumber: null as string | null,
        nationalID: null as string | null,
        bankAccountNumber: null as string | null,
        companyIDs: null as string[] | null,
    };
    if (!term) return empty;
    if (term?.includes(',')) {
        return { ...empty, companyIDs: term?.split(',').map((id: string) => id?.trim()) };
    }
    const digits = term.replace(/\D/g, '');
    if (getRegion() === Region.EG) {
        // Phone is checked before the (generic 11-digit) bank account number since
        // Egyptian mobile numbers are also 11 digits — the leading 01[0125] disambiguates.
        if (/^(?:0020|20)?0?1[0125]\d{8}$/.test(digits)) return { ...empty, phoneNumber: term };
        if (helper.regexValidation.accountNumber.test(term)) return { ...empty, bankAccountNumber: term };
        if (helper.regexValidation.nationalId.test(digits)) return { ...empty, nationalID: digits };
        return { ...empty, staffID: term };
    }
    if (/^[12]\d{9}$/.test(digits)) return { ...empty, nationalID: digits };
    if (/^(?:\+?966|0)?5\d{8}$/.test(digits)) return { ...empty, phoneNumber: term };
    return { ...empty, staffID: term };
};

export const formatOutstandingAmount = (amount?: number) => {
    const region = getRegion();
    if (amount === undefined || amount === null) return 'N/A';
    return `${helper.amountFormatter(amount)} ${region === Region.SE ? 'SAR' : 'EGP'}`;
};
